import React, { useState, useEffect } from 'react';
import { useNavigate, Outlet, Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Users, FileText, Ticket, Bell, Settings, LogOut, 
  Menu, X, ChevronDown, Building2, UserCircle, Target, TrendingUp, BarChart3, GitBranch, FileSpreadsheet, Trophy, Palette,
  Search, ChevronRight, ClipboardList, Mail, MapPin, CalendarClock, BookOpen, MessageSquare, Award, Phone
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import AIChatbot from '@/components/AIChatbot';
import BackgroundLocationTracker from '@/components/BackgroundLocationTracker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_MENUS = {
  cmd: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Palette, label: 'Content Management', path: '/dashboard/cms' },
    { icon: FileSpreadsheet, label: 'Excel Reports', path: '/dashboard/excel' },
    { icon: GitBranch, label: 'Team Hierarchy', path: '/dashboard/hierarchy' },
    { icon: BookOpen, label: 'CP/ADM/DM Workbooks', path: '/dashboard/workbooks' },
    { icon: MessageSquare, label: 'Messaging Center', path: '/dashboard/messaging' },
    { icon: Trophy, label: 'Contests', path: '/dashboard/contests' },
    { icon: Ticket, label: 'Department Tickets', path: '/dashboard/tickets' },
  ],
  super_admin: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Palette, label: 'Content Management', path: '/dashboard/cms' },
    { icon: FileSpreadsheet, label: 'Excel Reports', path: '/dashboard/excel' },
    { icon: GitBranch, label: 'Team Hierarchy', path: '/dashboard/hierarchy' },
    { icon: MessageSquare, label: 'Messaging Center', path: '/dashboard/messaging' },
    { icon: Trophy, label: 'Contests', path: '/dashboard/contests' },
    { icon: Users, label: 'User Management', path: '/dashboard/users' },
    { icon: BookOpen, label: 'CP/ADM/DM Workbooks', path: '/dashboard/workbooks' },
    { icon: Ticket, label: 'Department Tickets', path: '/dashboard/tickets' },
    { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
  ],
  department_head: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: FileSpreadsheet, label: 'Excel Reports', path: '/dashboard/excel' },
    { icon: GitBranch, label: 'Team Hierarchy', path: '/dashboard/hierarchy' },
    { icon: Trophy, label: 'Contests', path: '/dashboard/contests' },
    { icon: Ticket, label: 'Department Tickets', path: '/dashboard/tickets' },
  ],
  dm: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Phone, label: 'AI Calls', path: '/dashboard/main?section=ai_calls' },
    { icon: FileSpreadsheet, label: 'Excel Reports', path: '/dashboard/excel' },
    { icon: GitBranch, label: 'My Team', path: '/dashboard/hierarchy' },
    { icon: Trophy, label: 'Contests', path: '/dashboard/contests' },
    { icon: Ticket, label: 'Tickets', path: '/dashboard/tickets' },
  ],
  adm: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: FileSpreadsheet, label: 'Excel Reports', path: '/dashboard/excel' },
    { icon: GitBranch, label: 'My Team', path: '/dashboard/hierarchy' },
    { icon: Trophy, label: 'Contests', path: '/dashboard/contests' },
    { icon: Ticket, label: 'Tickets', path: '/dashboard/tickets' },
  ],
  cp: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: GitBranch, label: 'Team Hierarchy', path: '/dashboard/hierarchy' },
    { icon: FileSpreadsheet, label: 'Excel Reports', path: '/dashboard/excel' },
    { icon: Trophy, label: 'Contests', path: '/dashboard/contests' },
    { icon: Ticket, label: 'Tickets', path: '/dashboard/tickets' },
  ],
  channel_admin: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: BookOpen, label: 'DM Workbooks', path: '/dashboard/workbooks' },
    { icon: FileSpreadsheet, label: 'Excel Reports', path: '/dashboard/excel' },
    { icon: GitBranch, label: 'Team Hierarchy', path: '/dashboard/hierarchy' },
  ],
  employee: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: GitBranch, label: 'Team Hierarchy', path: '/dashboard/hierarchy' },
    { icon: FileSpreadsheet, label: 'Excel Reports', path: '/dashboard/excel' },
    { icon: Trophy, label: 'Contests', path: '/dashboard/contests' },
    { icon: Ticket, label: 'Tickets', path: '/dashboard/tickets' },
  ],
  customer: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Building2, label: 'My Properties', path: '/dashboard/properties' },
    { icon: Ticket, label: 'Support Tickets', path: '/dashboard/tickets' },
  ],
};

const DEPARTMENT_LABELS = {
  admin: 'Admin Department',
  dm_channel: 'DM Channel',
  marketing: 'Marketing',
  legal: 'Legal Department',
  finance: 'Finance Department',
  operations: 'Operations & Development',
  architect: 'Architect Department',
  it: 'IT Department',
  hr: 'Human Resources',
  customer: 'Customer Portal',
  vehicle_maintenance: 'Car & Vehicle Maintenance',
};

const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [inprocessCount, setInprocessCount] = useState(0);
  const [loginAt] = useState(() => {
    // Persist first-render time as "logged in at" for this session
    const existing = localStorage.getItem('crm_login_at');
    if (existing) return new Date(existing);
    const now = new Date();
    localStorage.setItem('crm_login_at', now.toISOString());
    return now;
  });

  useEffect(() => {
    const token = localStorage.getItem('crm_token');
    const storedUser = localStorage.getItem('crm_user');
    
    if (!token || !storedUser) {
      navigate('/signin');
      return;
    }

    try {
      const u = JSON.parse(storedUser);
      setUser(u);
      setLoading(false);
      // Refresh profile from server to get latest designation/data
      axios.get(`${API}/crm/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => {
          const fresh = r.data;
          setUser(fresh);
          localStorage.setItem('crm_user', JSON.stringify(fresh));
        })
        .catch(() => {});
      // Fetch INPROCESS badge count for non-HR users
      if (u?.department !== 'hr' && u?.email) {
        fetch(`${API}/report-index/inprocess-count?email=${encodeURIComponent(u.email)}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
          .then(r => r.json())
          .then(d => setInprocessCount(d.count || 0))
          .catch(() => {});
      }
    } catch (e) {
      navigate('/signin');
    }
  }, [navigate]);

  const handleLogout = () => {
    const token = localStorage.getItem('crm_token');
    if (token) {
      // Record logout event for Sahil's report (fire and forget)
      axios.post(`${API}/location/employee-logout`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
      // Mark location as offline
      axios.post(`${API}/location/offline`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    localStorage.removeItem('crm_token');
    localStorage.removeItem('crm_user');
    localStorage.removeItem('crm_login_at');
    toast.success('Logged out successfully');
    navigate('/signin');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  const baseMenuItems = ROLE_MENUS[user?.role] || ROLE_MENUS.employee;

  // Inject "Email" entry right after "Dashboard" for every role (per Feb 26
  // 2026 CMD request — all 41 employees get Gmail access in the CRM).
  const withEmail = (() => {
    const idx = baseMenuItems.findIndex(item => item.path === '/dashboard');
    const insert = { icon: Mail, label: 'Email', path: '/dashboard/email' };
    const pos = idx >= 0 ? idx + 1 : 1;
    return [...baseMenuItems.slice(0, pos), insert, ...baseMenuItems.slice(pos)];
  })();

  // Inject "Live Location" entry right after "Email" for every role.
  const withLocation = (() => {
    const idx = withEmail.findIndex(item => item.path === '/dashboard/email');
    const insert = { icon: MapPin, label: 'Live Location', path: '/dashboard/location' };
    const pos = idx >= 0 ? idx + 1 : 2;
    return [...withEmail.slice(0, pos), insert, ...withEmail.slice(pos)];
  })();

  // Inject "Attendance" (HR / Admin / CMD only) after Live Location.
  const withAttendance = (() => {
    const isHRAdmin = ['cmd', 'super_admin'].includes(user?.role) || (user?.department || '').toLowerCase() === 'hr';
    if (!isHRAdmin) return withLocation;
    const idx = withLocation.findIndex(item => item.path === '/dashboard/location');
    const insert = { icon: CalendarClock, label: 'Attendance', path: '/dashboard/attendance' };
    const pos = idx >= 0 ? idx + 1 : withLocation.length;
    return [...withLocation.slice(0, pos), insert, ...withLocation.slice(pos)];
  })();

  // Inject "Index 1 & 2" entry right after "Live Location" for ALL roles (every
  // employee needs access to their daily attendance checklist and Information Form).
  const withIndex12 = (() => {
    const isCmd = user?.role === 'cmd' || user?.role === 'super_admin';
    if (isCmd) return withAttendance; // CMD/super_admin manage via their own dashboard
    const idx = withAttendance.findIndex(item => item.path === '/dashboard/location');
    const insert = { icon: ClipboardList, label: 'Index 1 & 2', path: '/dashboard/checklist' };
    const pos = idx >= 0 ? idx + 1 : withAttendance.length;
    return [...withAttendance.slice(0, pos), insert, ...withAttendance.slice(pos)];
  })();

  const menuItems = withIndex12;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-[#090E17] text-white flex items-center justify-between px-4 z-50 shadow-lg">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-amber-700 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-lg tracking-tight" style={{ fontFamily: 'Manrope' }}>DBK CRM</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button className="p-2 hover:bg-white/10 rounded-lg transition-colors relative">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-amber-500 rounded-full"></span>
          </button>
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center text-sm font-bold">
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </div>
        </div>
      </div>

      {/* Sidebar - Modern Dark Design */}
      <aside className={`
        fixed top-0 left-0 h-full bg-[#090E17] text-white z-40 transition-all duration-300 ease-out
        ${sidebarOpen ? 'w-64' : 'w-20'}
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        shadow-2xl
      `}>
        {/* Logo Section */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
          {sidebarOpen ? (
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-700 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20 group-hover:shadow-amber-500/40 transition-shadow">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="font-bold text-lg tracking-tight block" style={{ fontFamily: 'Manrope' }}>DBK CRM</span>
                <span className="text-[10px] text-slate-400 uppercase tracking-wider">Real Estate</span>
              </div>
            </Link>
          ) : (
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-700 rounded-xl flex items-center justify-center mx-auto">
              <Building2 className="w-6 h-6 text-white" />
            </div>
          )}
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden lg:flex p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform duration-300 ${sidebarOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* User Profile Card */}
        <div className={`p-4 border-b border-white/10 ${!sidebarOpen && 'py-4 px-2'}`}>
          <div className={`flex items-center gap-3 ${!sidebarOpen && 'justify-center'}`}>
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center font-bold text-lg shadow-lg">
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
            {sidebarOpen && (
              <div className="overflow-hidden flex-1">
                <p className="font-bold truncate text-white text-sm leading-tight" style={{ fontFamily: 'Manrope' }}>{user?.designation || (user?.firstName + ' ' + user?.lastName)}</p>
                <p className="text-xs text-slate-300 truncate mt-0.5">{user?.firstName} {user?.lastName}</p>
                {/* Login date & time — placed above employeeId */}
                <div className="mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-400/30" data-testid="sidebar-login-time">
                  <CalendarClock className="w-2.5 h-2.5 text-emerald-300" />
                  <span className="text-[9px] text-emerald-200 font-semibold tracking-wide tabular-nums">
                    {loginAt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} · {loginAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                  </span>
                </div>
                <p className="text-[10px] text-amber-500 uppercase tracking-wider mt-0.5">{user?.employeeId}</p>
              </div>
            )}
          </div>
        </div>

        {/* Search - Only when expanded */}
        {sidebarOpen && (
          <div className="px-4 py-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input 
                type="text" 
                placeholder="Quick search..." 
                className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/50 focus:bg-white/10 transition-all"
              />
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="p-3 flex-1 overflow-y-auto">
          <div className={`${sidebarOpen ? 'mb-2' : ''}`}>
            {sidebarOpen && <p className="text-[10px] text-slate-500 uppercase tracking-wider px-3 mb-2">Main Menu</p>}
          </div>
          {menuItems.map((item) => {
            const Icon = item.icon;
            const pathOnly = item.path.split('?')[0];
            const searchQuery = item.path.includes('?') ? '?' + item.path.split('?')[1] : null;
            const isActive = (searchQuery
              ? location.pathname === pathOnly && location.search === searchQuery
              : location.pathname === pathOnly)
              || (item.path === '/dashboard' && location.pathname === '/dashboard/main');
            const isDashHome = item.path === '/dashboard';
            const showBadge = isDashHome && inprocessCount > 0;
            const isAICallsItem = item.path.includes('section=ai_calls');
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-all duration-200 group relative
                  ${isActive 
                    ? isAICallsItem
                      ? 'bg-gradient-to-r from-violet-600 to-violet-700 text-white shadow-lg shadow-violet-600/20'
                      : 'bg-gradient-to-r from-amber-600 to-amber-700 text-white shadow-lg shadow-amber-600/20' 
                    : isAICallsItem
                      ? 'text-violet-400 hover:bg-violet-500/10 hover:text-violet-300'
                      : 'text-slate-400 hover:bg-white/5 hover:text-white'}
                  ${!sidebarOpen && 'justify-center px-2'}
                `}
              >
                <div className="relative flex-shrink-0">
                  <Icon className={`w-5 h-5 ${isActive ? 'text-white' : isAICallsItem ? 'text-violet-500 group-hover:text-violet-300' : 'text-slate-500 group-hover:text-amber-500'} transition-colors`} />
                  {showBadge && !sidebarOpen && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-3.5 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5 leading-none">
                      {inprocessCount > 99 ? '99+' : inprocessCount}
                    </span>
                  )}
                </div>
                {sidebarOpen && (
                  <span className="text-sm font-medium flex-1">{item.label}</span>
                )}
                {showBadge && sidebarOpen && (
                  <span className="ml-auto min-w-[20px] h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1.5 leading-none">
                    {inprocessCount > 99 ? '99+' : inprocessCount}
                  </span>
                )}
                {isActive && sidebarOpen && !showBadge && (
                  <div className="absolute right-3 w-1.5 h-1.5 bg-white rounded-full"></div>
                )}
                {!sidebarOpen && (
                  <div className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                    {item.label}{showBadge ? ` · ${inprocessCount} in-progress` : ''}
                  </div>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="p-3 border-t border-white/10">
          <button
            onClick={handleLogout}
            className={`
              flex items-center gap-3 px-3 py-2.5 rounded-lg w-full transition-all duration-200 group
              text-slate-400 hover:bg-rose-500/10 hover:text-rose-400
              ${!sidebarOpen && 'justify-center px-2'}
            `}
          >
            <LogOut className="w-5 h-5 group-hover:text-rose-400 transition-colors" />
            {sidebarOpen && <span className="text-sm font-medium">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`
        transition-all duration-300 ease-out pt-16 lg:pt-0 min-h-screen
        ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}
      `}>
        <Outlet context={{ user }} />
      </main>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      
      {/* AI Chatbot for CRM */}
      <AIChatbot context="crm" userId={user?.id} />
      {user && <BackgroundLocationTracker user={user} />}
    </div>
  );
};

export default DashboardLayout;
